/**
 * Smart Return Offers Settings Page (P2-A)
 * Dealer configuration for behavioral conversion assist
 * 
 * UI Updates (Jan 2026):
 * - Dealer context indicator with business name
 * - Sub-Master read-only access with dealer selector
 * - Master Admin requires dealer selection
 * - Design lock disclaimer
 */
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { 
  Bell, 
  Clock, 
  Target, 
  MessageSquare, 
  Phone, 
  Calendar,
  Car,
  DollarSign,
  Save,
  AlertCircle,
  CheckCircle,
  Settings,
  Loader2,
  Lock,
  Building2,
  ChevronDown,
  Info
} from "lucide-react";
import "./SmartReturnOffersPage.css";

const MODULE_OPTIONS = [
  { id: "vip_appointment", label: "VIP Appointment", icon: Calendar, description: "Schedule a priority appointment" },
  { id: "preapproval", label: "Quick Pre-Approval", icon: DollarSign, description: "Get financing pre-approval" },
  { id: "trade_value", label: "Trade-In Value", icon: Car, description: "Estimate trade-in value" },
  { id: "sales_contact", label: "Talk to Sales", icon: Phone, description: "Connect with sales team" },
  { id: "credit_check", label: "Credit Check", icon: CheckCircle, description: "Soft credit approval check" },
  { id: "request_callback", label: "Request Callback", icon: MessageSquare, description: "Request a callback from sales" },
];

const SIZE_OPTIONS = ["small", "medium", "large"];

const SmartReturnOffersPage = () => {
  const auth = useAuth();
  const toast = useToast();
  
  // Guard: Show loading state if auth context is not ready
  if (!auth) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="auth-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }
  
  const { apiFetch, user } = auth;
  
  // Guard: If apiFetch is not available, show login prompt
  if (!apiFetch) {
    return (
      <div className="flex flex-col items-center justify-center h-64" data-testid="login-required">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-800">Please Log In</h2>
        <p className="text-gray-600 mt-2">You need to be logged in to access this page.</p>
      </div>
    );
  }
  
  return <SmartReturnOffersContent apiFetch={apiFetch} toast={toast} user={user} />;
};

const SmartReturnOffersContent = ({ apiFetch, toast, user }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    gap_hours: 24,
    cooldown_hours: 72,
    intent_threshold_enabled: true,
    target_inventory: true,
    target_vdp: true,
    target_homepage: false,
    enabled_modules: ["vip_appointment", "sales_contact"],
    headline: "Welcome Back!",
    subtext: "We noticed you're interested. How can we help today?",
    disclaimer_text: "",
    button_labels: {
      vip_appointment: "Schedule VIP Appointment",
      preapproval: "Get Pre-Approved",
      trade_value: "Get Trade Value",
      sales_contact: "Talk to Sales",
      credit_check: "Check My Credit",
      request_callback: "Request Callback"
    },
    sales_phone: "",
    sales_sms: "",
    button_size: "medium",
    headline_size: "medium",
    primary_color: "#2563eb",
    secondary_color: "#10b981",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Dealer context state
  const [dealerName, setDealerName] = useState("");
  const [assignedDealers, setAssignedDealers] = useState([]);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [loadingDealers, setLoadingDealers] = useState(false);
  
  // Determine user role and access level
  const userRole = user?.role || "";
  const isSubMaster = userRole === "SUB_MASTER";
  const isMasterAdmin = userRole === "MASTER_ADMIN";
  const isReadOnly = isSubMaster;
  // Master Admin and Sub-Master MUST select a dealer explicitly
  // For Master Admin: always require selection (don't auto-use their own business)
  // For Sub-Master: require selection (they don't have a business_id)
  const needsDealerSelection = (isSubMaster || isMasterAdmin) && !selectedDealerId;

  // Load assigned dealers for Sub-Master
  useEffect(() => {
    const loadAssignedDealers = async () => {
      if (!isSubMaster) return;
      
      setLoadingDealers(true);
      try {
        const res = await apiFetch("/submaster/my-dealers", { method: "GET" });
        if (res.data?.dealers) {
          setAssignedDealers(res.data.dealers);
          // Auto-select first dealer if only one
          if (res.data.dealers.length === 1) {
            setSelectedDealerId(res.data.dealers[0].business_id);
            setDealerName(res.data.dealers[0].name);
          }
        }
      } catch (err) {
        console.error("Error loading assigned dealers:", err);
        toast?.error("Failed to load assigned dealers");
      } finally {
        setLoadingDealers(false);
      }
    };
    
    loadAssignedDealers();
  }, [apiFetch, isSubMaster, toast]);

  // Load all dealers for Master Admin
  useEffect(() => {
    const loadAllDealers = async () => {
      if (!isMasterAdmin) return;
      
      setLoadingDealers(true);
      try {
        const res = await apiFetch("/admin/dealers", { method: "GET" });
        if (res.data?.dealers || Array.isArray(res.data)) {
          const dealers = res.data?.dealers || res.data;
          setAssignedDealers(dealers);
        }
      } catch (err) {
        console.error("Error loading dealers:", err);
        // Fallback: try to load from businesses endpoint
        try {
          const res = await apiFetch("/settings/business-debug", { method: "GET" });
          if (res.data?.business_name) {
            setDealerName(res.data.business_name);
          }
        } catch (e) {
          // Ignore
        }
      } finally {
        setLoadingDealers(false);
      }
    };
    
    loadAllDealers();
  }, [apiFetch, isMasterAdmin]);

  // Load settings (with dealer context for Sub-Master/Master Admin)
  useEffect(() => {
    let isCancelled = false;
    let hasSucceeded = false;
    
    const loadSettings = async () => {
      // Skip if dealer selection is needed but not made
      if (needsDealerSelection && !selectedDealerId) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      // Clear any previous error on new load attempt
      setMessage(null);
      
      try {
        // Build headers for viewing a specific dealer (SUB_MASTER or MASTER_ADMIN)
        const headers = {};
        const viewingDealerId = selectedDealerId || localStorage.getItem("viewing_dealer_id");
        
        // Only add header for admin roles, not for regular dealers
        if ((isSubMaster || isMasterAdmin) && viewingDealerId) {
          headers["X-Viewing-Dealer"] = viewingDealerId;
        }
        
        // For Dealer Admin, clear any stale viewing_dealer_id
        if (!isSubMaster && !isMasterAdmin) {
          localStorage.removeItem("viewing_dealer_id");
        }
        
        const res = await apiFetch("/smart-offers/settings", { 
          method: "GET",
          headers 
        });
        
        // Check if component unmounted
        if (isCancelled) return;
        
        hasSucceeded = true;
        
        if (res.data) {
          setSettings(prev => ({ ...prev, ...res.data }));
        }
        
        // Fetch dealer name separately (non-blocking)
        if (!dealerName) {
          apiFetch("/settings/business-debug", { method: "GET", headers })
            .then(bizRes => {
              if (!isCancelled && bizRes.data?.business_name) {
                setDealerName(bizRes.data.business_name);
              }
            })
            .catch(() => {}); // Ignore errors for dealer name
        }
      } catch (err) {
        // Only handle error if not cancelled and not already succeeded
        if (isCancelled || hasSucceeded) return;
        
        console.error("[SmartReturnOffers] Load error:", err?.response?.status, err?.message);
        
        // Don't show toast for auth errors (handled by AuthContext) or cancelled requests
        const status = err?.response?.status;
        if (status === 401 || status === 403) return;
        
        // Don't show toast for network errors that might be transient
        if (!status && err?.message?.includes('Network')) return;
        
        // Handle 404 - likely no settings exist yet, which is OK
        if (status === 404) {
          // Settings don't exist yet - use defaults, no error needed
          return;
        }
        
        // Only show error for actual server errors
        if (status >= 500) {
          setMessage({ type: "error", text: "Server error. Please try again." });
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    
    loadSettings();
    
    return () => {
      isCancelled = true;
    };
  }, [apiFetch, selectedDealerId, isSubMaster, isMasterAdmin, needsDealerSelection]);
  
  // Separate effect for dealer name to avoid re-fetching settings
  useEffect(() => {
    if (dealerName) return; // Already have dealer name
    if (needsDealerSelection) return; // No dealer selected yet
    
    const fetchDealerName = async () => {
      try {
        const headers = {};
        const viewingDealerId = selectedDealerId || localStorage.getItem("viewing_dealer_id");
        if ((isSubMaster || isMasterAdmin) && viewingDealerId) {
          headers["X-Viewing-Dealer"] = viewingDealerId;
        }
        
        const bizRes = await apiFetch("/settings/business-debug", { 
          method: "GET",
          headers 
        });
        if (bizRes.data?.business_name) {
          setDealerName(bizRes.data.business_name);
        }
      } catch (e) {
        // Ignore - dealer name is optional
      }
    };
    
    fetchDealerName();
  }, [apiFetch, selectedDealerId, isSubMaster, isMasterAdmin, needsDealerSelection, dealerName]);

  const saveSettings = async () => {
    if (isReadOnly) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      // Build headers for MASTER_ADMIN viewing a specific dealer
      const headers = {};
      const viewingDealerId = selectedDealerId || localStorage.getItem("viewing_dealer_id");
      
      if (isMasterAdmin && viewingDealerId) {
        headers["X-Viewing-Dealer"] = viewingDealerId;
      }
      
      // For Dealer Admin, ensure no stale viewing header
      if (!isSubMaster && !isMasterAdmin) {
        localStorage.removeItem("viewing_dealer_id");
      }
      
      const response = await apiFetch("/smart-offers/settings", {
        method: "PUT",
        data: settings,
        headers,
      });
      
      // Verify success response
      if (response.data?.success || response.status === 200) {
        setMessage({ type: "success", text: "Settings saved successfully!" });
        toast?.success("Settings saved!");
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      console.error("[SmartReturnOffers] Save error:", err?.response?.status, err?.response?.data);
      
      // Extract meaningful error message
      let errorMsg = "Failed to save settings";
      if (err?.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err?.response?.status === 403) {
        errorMsg = "You don't have permission to save these settings";
      } else if (err?.response?.status >= 500) {
        errorMsg = "Server error. Please try again.";
      }
      
      setMessage({ type: "error", text: errorMsg });
      toast?.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    if (isReadOnly) return; // Prevent changes for read-only users
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleModuleToggle = (moduleId) => {
    if (isReadOnly) return; // Prevent changes for read-only users
    setSettings(prev => {
      const current = prev.enabled_modules || [];
      if (current.includes(moduleId)) {
        // Don't allow removing if it's the last one
        if (current.length <= 1) return prev;
        return { ...prev, enabled_modules: current.filter(m => m !== moduleId) };
      } else {
        // Max 4 modules
        if (current.length >= 4) return prev;
        return { ...prev, enabled_modules: [...current, moduleId] };
      }
    });
  };

  const handleButtonLabelChange = (moduleId, label) => {
    if (isReadOnly) return; // Prevent changes for read-only users
    setSettings(prev => ({
      ...prev,
      button_labels: { ...prev.button_labels, [moduleId]: label }
    }));
  };

  const handleDealerSelect = (dealer) => {
    const dealerId = dealer.business_id || dealer.id;
    
    // Store in localStorage FIRST so apiFetch can use it
    localStorage.setItem("viewing_dealer_id", dealerId);
    
    // Then update state to trigger re-render and settings load
    setSelectedDealerId(dealerId);
    setDealerName(dealer.name);
    setLoading(true);
  };

  // Show dealer selection prompt for Sub-Master/Master Admin without context
  if (needsDealerSelection) {
    return (
      <div className="settings-page" data-testid="smart-return-offers-page">
        <div className="settings-header">
          <div>
            <h1 className="flex items-center gap-2">
              Smart Return Offers
              {isSubMaster && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                  <Lock className="w-3 h-3" />
                  Read-Only Access
                </span>
              )}
            </h1>
            <p className="text-gray-600">
              Behavioral conversion assist for returning visitors
            </p>
          </div>
        </div>

        <div className="settings-section">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <Building2 className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Select a Dealer</h3>
            <p className="text-amber-700 mb-4">
              Smart Return Offers are configured per-dealer. Please select a dealer to {isSubMaster ? "view" : "view or edit"} settings.
            </p>
            
            {loadingDealers ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                <span className="ml-2 text-amber-700">Loading dealers...</span>
              </div>
            ) : assignedDealers.length > 0 ? (
              <div className="max-w-md mx-auto">
                <div className="space-y-2">
                  {assignedDealers.map(dealer => (
                    <button
                      key={dealer.business_id || dealer.id}
                      onClick={() => handleDealerSelect(dealer)}
                      className="w-full flex items-center justify-between p-3 bg-white border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors text-left"
                      data-testid={`dealer-select-${dealer.business_id || dealer.id}`}
                    >
                      <span className="font-medium text-gray-900">{dealer.name}</span>
                      <ChevronDown className="w-4 h-4 text-gray-400 transform -rotate-90" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-amber-600">No dealers available.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-spinner" data-testid="loading-spinner">
        Loading settings...
      </div>
    );
  }

  return (
    <div className="settings-page" data-testid="smart-return-offers-page">
      <div className="settings-header">
        <div>
          <h1 className="flex items-center gap-2">
            Smart Return Offers
            {isReadOnly && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                <Lock className="w-3 h-3" />
                Read-Only Access
              </span>
            )}
          </h1>
          <p className="text-gray-600">
            Behavioral conversion assist for returning visitors
          </p>
        </div>
        {/* Only show Save button for non-read-only users */}
        {!isReadOnly && (
          <button 
            className="btn-primary flex items-center gap-2"
            onClick={saveSettings}
            disabled={saving}
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        )}
      </div>

      {/* Dealer Context Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-testid="dealer-context-indicator">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600" />
          <span className="font-medium text-blue-900">
            Smart Return Offers for: {dealerName || "Loading..."}
          </span>
        </div>
        <p className="text-sm text-blue-700 mt-1 ml-7">
          Scoped to this dealer only.
        </p>
        
        {/* Dealer switcher for Sub-Master with multiple dealers */}
        {isSubMaster && assignedDealers.length > 1 && (
          <div className="mt-3 ml-7">
            <select
              value={selectedDealerId || ""}
              onChange={(e) => {
                const dealer = assignedDealers.find(d => d.business_id === e.target.value);
                if (dealer) handleDealerSelect(dealer);
              }}
              className="text-sm border border-blue-300 rounded px-2 py-1 bg-white"
              data-testid="dealer-switcher"
            >
              {assignedDealers.map(dealer => (
                <option key={dealer.business_id} value={dealer.business_id}>
                  {dealer.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Dealer Role Explanation - Only for Dealer Admin */}
      {!isSubMaster && !isMasterAdmin && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6" data-testid="dealer-permissions-info">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-700">What you can customize:</p>
              <ul className="text-sm text-gray-600 mt-1 list-disc list-inside space-y-0.5">
                <li>Enable/disable Smart Return Offers</li>
                <li>Choose which actions appear (1-4 buttons)</li>
                <li>Adjust trigger timing and page targeting</li>
                <li>Edit headline, subtext, and disclaimer wording</li>
              </ul>
              <p className="text-xs text-gray-500 mt-2 italic">
                Design and colors are managed by Verified Demand for optimal performance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Read-only notice for Sub-Master */}
      {isReadOnly && (
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6" data-testid="read-only-notice">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-700 font-medium">You have read-only access</p>
              <p className="text-sm text-gray-600 mt-1">
                Contact the dealer admin to make changes to these settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`message ${message.type}`} data-testid="message-banner">
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="settings-section">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Enable Smart Return Offers
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              Show conversion prompts to returning visitors after a period of inactivity
            </p>
          </div>
          <label className={`toggle-switch ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid="enable-toggle">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => handleChange("enabled", e.target.checked)}
              disabled={isReadOnly}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Trigger Settings */}
      <div className="settings-section">
        <h3 className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5" />
          Trigger Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="setting-row">
            <label>Inactivity Gap (hours)</label>
            <input
              type="number"
              min="1"
              max="168"
              value={settings.gap_hours}
              onChange={(e) => handleChange("gap_hours", parseInt(e.target.value) || 24)}
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="gap-hours-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Show offer after visitor has been away for this many hours
            </p>
          </div>
          
          <div className="setting-row">
            <label>Cooldown Period (hours)</label>
            <input
              type="number"
              min="1"
              max="720"
              value={settings.cooldown_hours}
              onChange={(e) => handleChange("cooldown_hours", parseInt(e.target.value) || 72)}
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="cooldown-hours-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Don&apos;t show again for this many hours after display
            </p>
          </div>
        </div>

        <div className="mt-4">
          <label className={`flex items-center gap-2 ${isReadOnly ? 'cursor-not-allowed' : ''}`}>
            <input
              type="checkbox"
              checked={settings.intent_threshold_enabled}
              onChange={(e) => handleChange("intent_threshold_enabled", e.target.checked)}
              disabled={isReadOnly}
              data-testid="intent-threshold-checkbox"
            />
            <span>Require prior engagement</span>
          </label>
          <p className="text-xs text-gray-500 ml-6">
            Only show to visitors who previously viewed vehicles or clicked unlock
          </p>
        </div>
      </div>

      {/* Page Targeting */}
      <div className="settings-section">
        <h3 className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5" />
          Page Targeting
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className={`flex items-center gap-2 p-3 border rounded-lg ${isReadOnly ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}`}>
            <input
              type="checkbox"
              checked={settings.target_inventory}
              onChange={(e) => handleChange("target_inventory", e.target.checked)}
              disabled={isReadOnly}
              data-testid="target-inventory-checkbox"
            />
            <div>
              <span className="font-medium">Inventory Pages</span>
              <p className="text-xs text-gray-500">SRP / Search results</p>
            </div>
          </label>
          
          <label className={`flex items-center gap-2 p-3 border rounded-lg ${isReadOnly ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}`}>
            <input
              type="checkbox"
              checked={settings.target_vdp}
              onChange={(e) => handleChange("target_vdp", e.target.checked)}
              disabled={isReadOnly}
              data-testid="target-vdp-checkbox"
            />
            <div>
              <span className="font-medium">Vehicle Detail Pages</span>
              <p className="text-xs text-gray-500">VDP / Single vehicle</p>
            </div>
          </label>
          
          <label className={`flex items-center gap-2 p-3 border rounded-lg ${isReadOnly ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}`}>
            <input
              type="checkbox"
              checked={settings.target_homepage}
              onChange={(e) => handleChange("target_homepage", e.target.checked)}
              disabled={isReadOnly}
              data-testid="target-homepage-checkbox"
            />
            <div>
              <span className="font-medium">Homepage</span>
              <p className="text-xs text-gray-500">Main landing page</p>
            </div>
          </label>
        </div>
      </div>

      {/* CTA Modules */}
      <div className="settings-section">
        <h3 className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5" />
          CTA Modules
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {isReadOnly ? "Currently enabled actions for returning visitors." : "Select 1-4 actions to offer returning visitors. Customize button labels below."}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MODULE_OPTIONS.map(module => {
            const Icon = module.icon;
            const isEnabled = settings.enabled_modules?.includes(module.id);
            return (
              <div 
                key={module.id}
                className={`p-4 border rounded-lg ${isEnabled ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} ${isReadOnly ? 'opacity-80' : ''}`}
              >
                <label className={`flex items-start gap-3 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => handleModuleToggle(module.id)}
                    className="mt-1"
                    disabled={isReadOnly}
                    data-testid={`module-${module.id}-checkbox`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-gray-600" />
                      <span className="font-medium">{module.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{module.description}</p>
                    
                    {isEnabled && (
                      <div className="mt-3">
                        <label className="text-xs text-gray-600">Button Label</label>
                        <input
                          type="text"
                          value={settings.button_labels?.[module.id] || module.label}
                          onChange={(e) => handleButtonLabelChange(module.id, e.target.value)}
                          className={`w-full mt-1 text-sm ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          disabled={isReadOnly}
                          data-testid={`module-${module.id}-label-input`}
                        />
                      </div>
                    )}
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Copy & Wording */}
      <div className="settings-section">
        <h3 className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5" />
          Copy & Wording
        </h3>
        
        <div className="space-y-4">
          <div className="setting-row">
            <label>Headline</label>
            <input
              type="text"
              value={settings.headline}
              onChange={(e) => handleChange("headline", e.target.value)}
              placeholder="Welcome Back!"
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="headline-input"
            />
          </div>
          
          <div className="setting-row">
            <label>Subtext</label>
            <textarea
              value={settings.subtext || ""}
              onChange={(e) => handleChange("subtext", e.target.value)}
              placeholder="We noticed you're interested. How can we help today?"
              rows={2}
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="subtext-input"
            />
          </div>
          
          <div className="setting-row">
            <label>Disclaimer (optional)</label>
            <textarea
              value={settings.disclaimer_text || ""}
              onChange={(e) => handleChange("disclaimer_text", e.target.value)}
              placeholder="Optional disclaimer text..."
              rows={2}
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="disclaimer-input"
            />
          </div>
        </div>
      </div>

      {/* Contact Targets */}
      <div className="settings-section">
        <h3 className="flex items-center gap-2 mb-4">
          <Phone className="w-5 h-5" />
          Contact Targets
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="setting-row">
            <label>Sales Phone Number</label>
            <input
              type="tel"
              value={settings.sales_phone || ""}
              onChange={(e) => handleChange("sales_phone", e.target.value)}
              placeholder="(555) 123-4567"
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="sales-phone-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              For &quot;Talk to Sales&quot; call action
            </p>
          </div>
          
          <div className="setting-row">
            <label>Sales SMS Number (optional)</label>
            <input
              type="tel"
              value={settings.sales_sms || ""}
              onChange={(e) => handleChange("sales_sms", e.target.value)}
              placeholder="(555) 123-4567"
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="sales-sms-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              For SMS-based contact option
            </p>
          </div>
        </div>
      </div>

      {/* Display Settings */}
      <div className="settings-section">
        <h3 className="mb-4">Display Settings</h3>
        
        {/* Master Admin: Full Design Controls */}
        {isMasterAdmin && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4" data-testid="master-design-controls">
            <div className="flex items-center gap-2 text-emerald-800 mb-3">
              <Settings className="w-5 h-5" />
              <span className="font-medium">Master Admin Design Controls</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="setting-row">
                <label className="flex items-center gap-2">
                  Primary Color
                  <span className="text-xs text-gray-500">(buttons & accents)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.primary_color || "#2563eb"}
                    onChange={(e) => handleChange("primary_color", e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    data-testid="primary-color-picker"
                  />
                  <input
                    type="text"
                    value={settings.primary_color || "#2563eb"}
                    onChange={(e) => handleChange("primary_color", e.target.value)}
                    placeholder="#2563eb"
                    className="flex-1 px-3 py-2 border rounded text-sm font-mono"
                    data-testid="primary-color-input"
                  />
                </div>
              </div>
              
              <div className="setting-row">
                <label className="flex items-center gap-2">
                  Secondary Color
                  <span className="text-xs text-gray-500">(optional)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.secondary_color || "#10b981"}
                    onChange={(e) => handleChange("secondary_color", e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                    data-testid="secondary-color-picker"
                  />
                  <input
                    type="text"
                    value={settings.secondary_color || "#10b981"}
                    onChange={(e) => handleChange("secondary_color", e.target.value)}
                    placeholder="#10b981"
                    className="flex-1 px-3 py-2 border rounded text-sm font-mono"
                    data-testid="secondary-color-input"
                  />
                </div>
              </div>
            </div>
            
            <p className="text-xs text-emerald-700 mt-3">
              As Master Admin, you control design for all dealers. Changes affect the selected dealer&apos;s offer appearance.
            </p>
          </div>
        )}
        
        {/* Dealer Admin: Design Lock Disclaimer */}
        {!isMasterAdmin && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="design-lock-disclaimer">
            <div className="flex items-center gap-2 text-blue-800">
              <Lock className="w-5 h-5" />
              <span className="font-medium">Design Controlled by Platform</span>
            </div>
            <p className="text-sm text-blue-700 mt-1">
              Colors and design are managed by your platform administrator for brand consistency.
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="setting-row">
            <label>Button Size</label>
            <select
              value={settings.button_size}
              onChange={(e) => handleChange("button_size", e.target.value)}
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="button-size-select"
            >
              {SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size.charAt(0).toUpperCase() + size.slice(1)}</option>
              ))}
            </select>
          </div>
          
          <div className="setting-row">
            <label>Headline Size</label>
            <select
              value={settings.headline_size}
              onChange={(e) => handleChange("headline_size", e.target.value)}
              disabled={isReadOnly}
              className={isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}
              data-testid="headline-size-select"
            >
              {SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size.charAt(0).toUpperCase() + size.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="settings-section">
        <h3 className="mb-4">Preview</h3>
        <div className="border rounded-lg p-6 bg-gray-50">
          <div 
            className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-6"
            style={{ borderTop: `4px solid ${settings.secondary_color || '#10b981'}` }}
          >
            <h2 
              className={`font-bold text-center mb-2 ${
                settings.headline_size === 'small' ? 'text-lg' :
                settings.headline_size === 'large' ? 'text-2xl' : 'text-xl'
              }`}
              style={{ color: settings.primary_color }}
            >
              {settings.headline || "Welcome Back!"}
            </h2>
            <p className="text-gray-600 text-center text-sm mb-4">
              {settings.subtext || "How can we help today?"}
            </p>
            <div className="space-y-2">
              {(settings.enabled_modules || []).map((moduleId, index) => {
                const module = MODULE_OPTIONS.find(m => m.id === moduleId);
                const Icon = module?.icon || MessageSquare;
                const label = settings.button_labels?.[moduleId] || module?.label || moduleId;
                // Alternate between primary and secondary color for buttons
                const buttonColor = index % 2 === 0 ? settings.primary_color : settings.secondary_color;
                return (
                  <button
                    key={moduleId}
                    className={`w-full flex items-center justify-center gap-2 rounded-lg text-white ${
                      settings.button_size === 'small' ? 'py-2 px-3 text-sm' :
                      settings.button_size === 'large' ? 'py-4 px-6 text-lg' : 'py-3 px-4'
                    }`}
                    style={{ backgroundColor: buttonColor }}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                );
              })}
            </div>
            {settings.disclaimer_text && (
              <p className="text-xs text-gray-400 text-center mt-4">
                {settings.disclaimer_text}
              </p>
            )}
          </div>
        </div>
        
        {/* Color legend */}
        <div className="flex items-center justify-center gap-6 mt-3 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: settings.primary_color }}></span>
            <span>Primary</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: settings.secondary_color }}></span>
            <span>Secondary</span>
          </div>
        </div>
        
        {/* Design disclaimer at bottom of preview */}
        <p className="text-xs text-gray-500 text-center mt-2 italic">
          Design is controlled by Verified Demand for performance and consistency.
        </p>
      </div>
    </div>
  );
};

export default SmartReturnOffersPage;
